import { useCallback, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useInputs } from "hooks/useInputs";
import useInputDetails from "hooks/useInputDetails";
import useNodeSummaries from "hooks/useNodeSummaries";
import usePluginEntities from "hooks/usePluginEntities";
import { MISSING_BUCKET_NAME } from "views/Constants";
const useForwarderNodeTitles = (ids, fetchFn) => useQueries({
  queries: ids.map((id) => ({
    queryKey: ["forwarder", "node", id],
    queryFn: () => fetchFn(id),
    enabled: !!fetchFn,
    retry: false
  })),
  combine: (results) => Object.fromEntries(ids.map((id, idx) => {
    var _a;
    return [id, (_a = results[idx].data) == null ? void 0 : _a.title];
  }).filter(([, title]) => !!title))
});
const useUnresolvedIds = (ids, known) => useMemo(() => known ? ids.filter((id) => id && id !== MISSING_BUCKET_NAME && !known[id]) : [], [ids, known]);
export const useInputTitleResolver = (ids) => {
  const inputs = useInputs();
  const unresolvedIds = useUnresolvedIds(ids, inputs);
  const forwarderInputs = useMemo(() => unresolvedIds.map((id) => ({ id, type: "forwarder_input" })), [unresolvedIds]);
  const { resolvedById } = useInputDetails(forwarderInputs);
  return useCallback((id) => {
    var _a, _b, _c;
    return (_c = (_a = inputs == null ? void 0 : inputs[id]) == null ? void 0 : _a.title) != null ? _c : (_b = resolvedById[id]) == null ? void 0 : _b.title;
  }, [inputs, resolvedById]);
};
const formatNode = (node) => `${node.short_node_id} / ${node.hostname}`;
export const useNodeTitleResolver = (ids) => {
  var _a;
  const nodes = useNodeSummaries();
  const { fetchForwarderNode } = (_a = usePluginEntities("forwarder")[0]) != null ? _a : {};
  const forwarderTitles = useForwarderNodeTitles(useUnresolvedIds(ids, nodes), fetchForwarderNode);
  return useCallback(
    (id) => (nodes == null ? void 0 : nodes[id]) ? formatNode(nodes[id]) : forwarderTitles[id],
    [nodes, forwarderTitles]
  );
};
